import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { ConfigurationService } from '@sc/data-access-configuration';
import { AuthService } from '@sc/feature-auth-okta';
import { ScreenUtils } from '@sc/utils';
import { combineLatest, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FitterRouterService {
  static readonly callToActionPageRoute = 'cta';
  static readonly loginPageRoute = `login`;
  static readonly planningPageRoute = `planning`;

  static readonly mobilePlanningPageRoute = `${FitterRouterService.planningPageRoute}/mobile`;
  static readonly mobilePlanningDossierRoute = `${FitterRouterService.mobilePlanningPageRoute}/dossier`;
  static readonly settingsPageRoute = `settings`;
  static readonly searchDossiersPageRoute = `dossiers-search`;
  static readonly dashboardPageRoute = `dashboard`;
  static readonly sidexaPageRoute = 'sidexa';
  static readonly preferences = 'preferences';
  static readonly sitePlan = 'site-plan';

  constructor(
    private readonly authOktaService: AuthService,
    private readonly configurationService: ConfigurationService,
    private readonly router: Router
  ) {}

  getRedirectRoute() {
    return combineLatest([
      this.authOktaService.userIsFitter$(),
      this.authOktaService.userHasFitterPlanningOktaAccess$(
        this.configurationService.getConfig().id
      ),
    ]).pipe(
      map(([userIsFitter, userHasPlanningAccess]) => {
        if (userIsFitter && userHasPlanningAccess) {
          return ScreenUtils.isLargeDesktopResolution()
            ? FitterRouterService.planningPageRoute
            : FitterRouterService.mobilePlanningPageRoute;
        } else {
          return FitterRouterService.loginPageRoute;
        }
      })
    );
  }

  goToPlanning(dossierId?: string, forceEdit?: boolean, newTab?: boolean) {
    let usedRoute = FitterRouterService.planningPageRoute;

    const queryParams: Params = {
      dossier: dossierId,
      forceEdit: +forceEdit || 0,
    };

    if (!ScreenUtils.isLargeDesktopResolution()) {
      usedRoute = FitterRouterService.mobilePlanningPageRoute;
    }

    if (newTab) {
      const windowRoute = this.router.serializeUrl(
        this.router.createUrlTree([usedRoute], { queryParams: queryParams })
      );

      window.open(windowRoute, '_blank');

      return Promise.resolve();
    }

    return this.router
      .navigate([usedRoute], { queryParams: queryParams })
      .catch((reason) => console.error(reason));
  }

  goToPlanningNewTab(dossierId?: string, forceEdit?: boolean) {
    return this.goToPlanning(dossierId, forceEdit, true);
  }

  goToDossier(dossierId: string, forceEdit?: boolean, newTab?: boolean) {
    if (ScreenUtils.isMobileResolution()) {
      this.goToMobileDossier(dossierId);
    } else {
      this.goToPlanning(dossierId, forceEdit, newTab);
    }
  }

  goToMobileDossier(dossierId: string) {
    const previousUrl = this.router.url;
    return this.router
      .navigate([FitterRouterService.mobilePlanningDossierRoute, dossierId], {
        queryParams: { previousUrl },
      })
      .catch((reason) => console.error(reason));
  }

  goToPreviousPage(previousUrl?: string) {
    let usedUrl = previousUrl;
    if (!usedUrl) {
      usedUrl = this.router.parseUrl(this.router.url)?.queryParams?.previousUrl;
    }

    return this.router.navigateByUrl(usedUrl);
  }

  goToSettings() {
    return this.router
      .navigate([FitterRouterService.settingsPageRoute])
      .catch((reason) => console.error(reason));
  }

  goToSearchDossiers() {
    return this.router
      .navigate([FitterRouterService.searchDossiersPageRoute])
      .catch((reason) => console.error(reason));
  }

  gotToMobileCalendar(fitterId: string) {
    return this.router
      .navigate([FitterRouterService.mobilePlanningPageRoute, fitterId])
      .catch((reason) => console.error(reason));
  }

  goToDashboard() {
    return this.router
      .navigate([FitterRouterService.dashboardPageRoute])
      .catch((reason) => console.error(reason));
  }

  goToSettingsDetailsFitter(id: string) {
    return this.router
      .navigate([FitterRouterService.settingsPageRoute, id])
      .catch((reason) => console.error(reason));
  }

  goToSidexaNewTab(licensePlate: string) {
    return this.router
      .navigate([])
      .then((result) =>
        window.open(
          `${FitterRouterService.sidexaPageRoute}/${licensePlate}`,
          '_blank'
        )
      )
      .catch((reason) => console.error(reason));
  }

  goToPreferences() {
    const previousUrl = this.router.url;
    return this.router
      .navigate([FitterRouterService.preferences], {
        queryParams: { previousUrl },
      })
      .catch((reason) => console.error(reason));
  }

  goToSitePlan() {
    const previousUrl = this.router.url;
    return this.router
      .navigate([FitterRouterService.sitePlan], {
        queryParams: { previousUrl },
      })
      .catch((reason) => console.error(reason));
  }
}
